import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "./queryKeys";
import { fetchAllVariablesForAgent } from "./variableCascade";

// ASSUMED(30s foreground refresh keeps mutable declarations current without a variable SSE message.)
const DECLARATION_REFRESH_MS = 30_000;

/** One complete, cancellable declaration read shared by every authoring surface. */
export function useVariableDeclarations(agentId: string, enabled = true) {
  const client = useQueryClient();
  const canRead = enabled && agentId.length > 0;
  const query = useQuery({
    enabled: canRead,
    queryKey: queryKeys.variables.allForAgent(agentId),
    queryFn: ({ signal }) => fetchAllVariablesForAgent(agentId, signal),
    staleTime: 0,
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
    refetchInterval: DECLARATION_REFRESH_MS,
    refetchIntervalInBackground: false,
  });

  // TanStack observes visibility changes. Window focus also covers returning
  // from another browser window where a declaration was created or revised.
  useEffect(() => {
    if (!canRead) return;
    const refresh = () => {
      if (document.visibilityState !== "visible") return;
      void client.refetchQueries(
        { queryKey: queryKeys.variables.allForAgent(agentId), exact: true },
        { cancelRefetch: false },
      );
    };
    window.addEventListener("focus", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
    };
  }, [agentId, canRead, client]);

  return query;
}
